// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { formatHumanName, type MedplumClient } from '@medplum/core';
import type { Patient, Practitioner } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import type {
  AvailabilitySearchResult,
  SchedulingClient,
  SchedulingScope,
  SchedulingTransport,
  SchedulingTransportOptions,
} from '@polarissleep/scheduling-next';
import { createSchedulingClient, getTimezoneExtensionValue, searchAvailability } from '@polarissleep/scheduling-next';
import { getConfig } from '../config';
import { getEarliestPatientBookableStart } from '../utils/patient-scheduling-notice';
import { createPatientAvailabilityRepository } from './repository';
import type { PatientAppointmentCandidate } from './types';

interface FindPatientAppointmentsParams {
  patient: Patient;
  appointmentType: AppointmentTypeDefinition;
  startDate: string;
  endDate: string;
  practitionerId?: string;
  signal?: AbortSignal;
}

export async function findPatientAppointments(
  medplum: MedplumClient,
  params: FindPatientAppointmentsParams
): Promise<PatientAppointmentCandidate[]> {
  const scope = getPatientSchedulingScope();
  const result = await searchPatientAvailability(medplum, params, scope);
  return mapCandidates(result);
}

export function createPatientSchedulingClient(medplum: MedplumClient): SchedulingClient {
  const transport: SchedulingTransport = {
    get: (path, options) => medplum.get(toMedplumUrl(medplum, path), toRequestOptions(options)),
    post: (path, body, options) =>
      medplum.post(toMedplumUrl(medplum, path), body, 'application/fhir+json', toRequestOptions(options)),
  };
  return createSchedulingClient(transport);
}

export function getPatientSchedulingScope(): SchedulingScope {
  const organizationId = getConfig().organizationId;
  const environment = getConfig().polarisEnv;
  if (!organizationId || !environment) {
    throw new Error('Patient v2 scheduling requires a tenant organization and Polaris environment.');
  }
  return { organizationId, environment };
}

async function searchPatientAvailability(
  medplum: MedplumClient,
  params: FindPatientAppointmentsParams,
  scope: SchedulingScope
): Promise<AvailabilitySearchResult> {
  return searchAvailability(
    createPatientAvailabilityRepository(medplum),
    createPatientSchedulingClient(medplum),
    {
      appointmentType: params.appointmentType,
      start: params.startDate,
      end: params.endDate,
      practitionerId: params.practitionerId,
      signal: params.signal,
      scope,
    },
    {
      minimumNotice: ({ now }) => getEarliestPatientBookableStart(now),
      now: () => new Date(),
    }
  );
}

function mapCandidates(result: AvailabilitySearchResult): PatientAppointmentCandidate[] {
  const providersById = new Map(
    result.providers.map((provider) => [
      `Practitioner/${provider.practitioner.id}`,
      {
        id: provider.practitioner.id ?? '',
        name: provider.practitioner.name?.[0] ? formatHumanName(provider.practitioner.name[0]) : 'Provider',
        timezone: requireProviderTimezone(provider.practitioner.id, provider.practitioner.extension),
      },
    ])
  );

  return result.appointments.map((appointment) => {
    const providerReference = appointment.participant?.find((participant) =>
      participant.actor?.reference?.startsWith('Practitioner/')
    )?.actor?.reference;
    const provider = providerReference ? providersById.get(providerReference) : undefined;
    if (!provider) {
      throw new Error('Proposed Appointment does not identify an eligible provider with a scheduling timezone.');
    }
    const serviceReference = appointment.serviceType?.flatMap((serviceType) =>
      (serviceType.extension ?? [])
        .filter((extension) => extension.url === 'https://medplum.com/fhir/service-type-reference')
        .map((extension) => extension.valueReference?.reference)
    )[0];
    const serviceId = serviceReference?.replace('HealthcareService/', '') ?? '';
    const service = result.providers.find(
      (candidate) => candidate.healthcareService.id === serviceId
    )?.healthcareService;
    const start = appointment.start ?? '';
    const end = appointment.end ?? '';
    const key = `${provider?.id ?? providerReference ?? 'provider'}|${serviceId}|${start}|${end}`;

    return {
      appointment,
      key,
      providerId: provider.id,
      providerName: provider.name,
      providerTimezone: provider.timezone,
      serviceId,
      serviceName: service?.name ?? appointment.serviceType?.[0]?.coding?.[0]?.display ?? 'Appointment',
      serviceCode: appointment.serviceType?.[0]?.coding?.[0]?.code ?? '',
    };
  });
}

function requireProviderTimezone(providerId: string | undefined, extensions: Practitioner['extension']): string {
  const timezone = getTimezoneExtensionValue(extensions);
  if (!timezone) {
    throw new Error(`Practitioner/${providerId ?? 'unknown'} has no valid scheduling timezone.`);
  }
  return timezone;
}

function toMedplumUrl(medplum: MedplumClient, path: string): URL {
  const parsed = new URL(path, 'https://patient-scheduling.invalid');
  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments[0] !== 'R4' || segments.length < 2) {
    throw new Error(`Unexpected scheduling operation path: ${path}`);
  }
  const url = medplum.fhirUrl(...segments.slice(1));
  url.search = parsed.search;
  return url;
}

function toRequestOptions(options: SchedulingTransportOptions | undefined): RequestInit | undefined {
  return options?.signal ? { signal: options.signal } : undefined;
}
